import { useCallback, useEffect, useState } from "react";
import { BellRing, Volume2, MapPin, CheckCircle2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getAlarmCtx } from "@/utils/alarmAudio";

const SEEN_KEY = "woma_courier_perm_onboarded";
const SESSION_KEY = "woma_courier_perm_reminded";
export const ALARM_KEY = "woma_alarm_sound_ok";

const geoState = async () => {
  try {
    const p = await navigator.permissions?.query({ name: "geolocation" });
    return p?.state || "prompt";
  } catch (e) {
    return "prompt";
  }
};

const notifState = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

// Bip court de test : débloque l'audio du navigateur pour l'alarme des nouvelles courses
const testAlarm = () => {
  const ctx = getAlarmCtx();
  if (!ctx) return false;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.value = 1100;
  gain.gain.value = 0.6;
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.6);
  navigator.vibrate?.([400, 100, 400]);
  return true;
};

const HELP = {
  notif: "Notifications bloquées : ouvrez les réglages du navigateur (icône cadenas à côté de l'adresse) → Notifications → Autoriser. Sur Android : Paramètres → Applications → Chrome → Notifications.",
  geo: "Localisation bloquée : réglages du navigateur (cadenas) → Position → Autoriser. Sur le téléphone : Paramètres → Localisation → activée, précision élevée.",
};

const PermRow = ({ Icon, title, why, state, onEnable, testId, help }) => {
  const ok = state === "granted";
  return (
    <div className={`rounded-2xl border p-3 space-y-2 ${ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-white/5"}`} data-testid={`perm-${testId}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 shrink-0 ${ok ? "text-emerald-400" : "text-[#D4AF37]"}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black">{title}</p>
          <p className="text-[11px] text-slate-400">{why}</p>
        </div>
        {ok || state === "denied" ? (ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" data-testid={`perm-${testId}-ok`} /> : <span className="shrink-0 text-[10px] font-black uppercase text-rose-300" data-testid={`perm-${testId}-denied`}>Bloquée</span>) : (
          <button type="button" onClick={onEnable} className="shrink-0 rounded-full bg-[#D4AF37] text-[#0F172A] px-3 py-1.5 text-[10px] font-black uppercase" data-testid={`perm-${testId}-btn`}>Activer maintenant</button>
        )}
      </div>
      {state === "denied" && <p className="text-[11px] text-rose-300 flex gap-2" data-testid={`perm-${testId}-help`}><ShieldAlert className="w-4 h-4 shrink-0" />{help}</p>}
    </div>
  );
};

// Onboarding livreur : notifications prioritaires, alarme sonore, géolocalisation.
// 1re connexion : affiché ; ensuite rappel à chaque connexion tant qu'une permission manque.
export const CourierPermissionsOnboarding = () => {
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState(notifState());
  const [geo, setGeo] = useState("prompt");
  const [sound, setSound] = useState(sessionStorage.getItem(ALARM_KEY) ? "granted" : "prompt");

  const refresh = useCallback(async () => {
    setNotif(notifState());
    setGeo(await geoState());
  }, []);

  useEffect(() => {
    (async () => {
      const g = await geoState();
      setGeo(g);
      const missing = notifState() !== "granted" || g !== "granted";
      const first = !localStorage.getItem(SEEN_KEY);
      if (first || (missing && !sessionStorage.getItem(SESSION_KEY))) setOpen(true);
    })();
  }, []);

  const close = () => {
    localStorage.setItem(SEEN_KEY, "1");
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
  };

  const enableNotif = async () => {
    if (typeof Notification === "undefined") return;
    await Notification.requestPermission();
    refresh();
  };
  const enableGeo = () => navigator.geolocation?.getCurrentPosition(() => refresh(), () => refresh(), { enableHighAccuracy: true, timeout: 10000 });
  const enableSound = () => {
    if (testAlarm()) {
      sessionStorage.setItem(ALARM_KEY, "1");
      setSound("granted");
    }
  };

  const allOk = notif === "granted" && geo === "granted" && sound === "granted";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#0B1220] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="courier-perm-onboarding">
        <DialogHeader>
          <DialogTitle className="text-left">Prêt à recevoir vos courses ?</DialogTitle>
          <DialogDescription className="text-left text-slate-300">
            Pour recevoir vos commandes en temps réel, même écran verrouillé, activez ces 3 autorisations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <PermRow Icon={BellRing} title="Notifications prioritaires" why="Une alerte plein écran avec ACCEPTER / REFUSER, même téléphone verrouillé." state={notif} onEnable={enableNotif} testId="notifications" help={HELP.notif} />
          <PermRow Icon={Volume2} title="Alarme sonore prioritaire" why="Une sirène forte + vibration pendant 30 s à chaque nouvelle course. Montez le volume du téléphone." state={sound} onEnable={enableSound} testId="sound" />
          <PermRow Icon={MapPin} title="Géolocalisation" why="Pour recevoir les courses proches de vous et guider le client. Laissez WÔMA ouvert pendant votre service." state={geo} onEnable={enableGeo} testId="geolocation" help={HELP.geo} />
        </div>
        <p className="text-[10px] text-slate-500">Astuce : désactivez le mode silencieux et l'économie de batterie pour le navigateur pendant votre service.</p>
        <button type="button" onClick={close} className={`w-full rounded-full py-3 text-xs font-black uppercase tracking-widest ${allOk ? "bg-emerald-500 text-white" : "bg-white/10"}`} data-testid="courier-perm-close-btn">
          {allOk ? "C'est parti !" : "Plus tard"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
